import React, { useState, useEffect, useCallback } from "react";
import ProductionSystemChart from "./ProductionChart";
import { populationRecordsApi } from "../../api/populationRecordsApi";
import { useCollectionSync } from "../../hooks/useCollectionSync";

function formatTotal(n) {
  if (!n) return '0';
  if (n >= 1e9) return (n / 1e9).toFixed(1).replace(/\.0$/, '') + 'B';
  if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
  return n.toLocaleString();
}

function useAnimatedCounter(target, duration = 1200) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!target) { setCount(0); return; }
    const start = performance.now();
    let frame;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setCount(Math.floor(eased * target));
      if (p < 1) frame = requestAnimationFrame(tick);
      else setCount(target);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);
  return count;
}

export default function SummaryCards() {
  const [records, setRecords] = useState([]);
  const [totalPopulation, setTotalPopulation] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadRecords = useCallback(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      populationRecordsApi.listAll({ per_page: 200 }),
      populationRecordsApi.totalHeadcount(),
    ])
      .then(([payload, total]) => {
        if (!active) return;
        setRecords(Array.isArray(payload?.items) ? payload.items : []);
        setTotalPopulation(Number(total) || 0);
      })
      .catch(() => {
        if (active) {
          setRecords([]);
          setTotalPopulation(0);
        }
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const cancel = loadRecords();
    return () => { if (typeof cancel === 'function') cancel(); };
  }, [loadRecords]);

  useCollectionSync(populationRecordsApi.collectionKey, () => {
    loadRecords();
  });

  // ── Derived stats ───────────────────────────────────────────────────────────
  const activeStatesCount = new Set(
    records.map((r) => r.state).filter(Boolean)
  ).size;

  // Production system breakdown by population count
  const prodTotals = {};
  let grandTotal = 0;
  records.forEach((r) => {
    const sys = r.productionSystem || 'Other';
    const cnt = Number(r.populationCount) || 0;
    prodTotals[sys] = (prodTotals[sys] || 0) + cnt;
    grandTotal += cnt;
  });

  // Build chart data — round each to integer, never show 0%
  const prodData = {};
  Object.entries(prodTotals).forEach(([name, cnt]) => {
    const pct = grandTotal > 0 ? Math.round((cnt / grandTotal) * 100) : 0;
    if (pct > 0) prodData[name] = { pct };
  });

  const animatedStates = useAnimatedCounter(activeStatesCount);
  const TOTAL_STATES = 37;
  const statesPct = TOTAL_STATES > 0 ? Math.round((activeStatesCount / TOTAL_STATES) * 100) : 0;
  const statesColor =
    activeStatesCount >= TOTAL_STATES - 1 ? 'text-emerald-600' :
    activeStatesCount >= Math.floor(TOTAL_STATES / 2) ? 'text-amber-500' :
    'text-gray-900';

  const card = "bg-gray-50 border border-gray-200 rounded-xl p-5";
  const skeleton = "text-[28px] font-black text-gray-200 leading-none animate-pulse";

  return (
    <div className="grid grid-cols-3 gap-4 mb-6">

      {/* ── Card 1: Total National Livestock Population ── */}
      <div className={card}>
        <p className="text-sm text-gray-500 mb-1 font-bold">Total National Livestock Population</p>
        {loading ? (
          <p className={skeleton}>—</p>
        ) : (
          <p className="text-[28px] font-black text-emerald-600 leading-none">
            {formatTotal(totalPopulation)}
          </p>
        )}
        <p className="text-[8px] text-gray-400 mt-1">
          Sum of all population counts across submitted records
        </p>
      </div>

      {/* ── Card 2: Active States Reporting ── */}
      <div className={card}>
        <p className="text-sm text-gray-500 mb-1 font-bold">Active States Reporting</p>
        {loading ? (
          <p className={skeleton}>—</p>
        ) : (
          <>
            <p className={`text-[28px] font-black leading-none ${statesColor}`}>
              {animatedStates} {animatedStates === 1 ? 'State' : 'States'}
            </p>
            <p className="text-[10px] mt-2 text-gray-400">
              {activeStatesCount} out of {TOTAL_STATES} states have records ({statesPct}%)
            </p>
          </>
        )}
      </div>

      {/* ── Card 3: By Production System ── */}
      <div className={card}>
        <p className="text-sm text-gray-500 font-bold">By Production System</p>
        {loading ? (
          <div className="flex items-center justify-center h-32 text-gray-300 text-xs animate-pulse">
            Loading…
          </div>
        ) : Object.keys(prodData).length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-400 text-xs">
            No records yet
          </div>
        ) : (
          <ProductionSystemChart data={prodData} />
        )}
      </div>

    </div>
  );
}
